import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

function enableWheelHorizontalScroll(rootEl) {
  const sliders = rootEl.querySelectorAll('.cart__upsell_collections, .css-slider-feel');
  if (!sliders.length) return () => {};

  const cleanup = [];
  sliders.forEach((slider) => {
    let snapRestoreTimer = null;
    let isSnappingDisabled = false;

    const onWheel = (e) => {
      // Ignore if user is already doing horizontal trackpad gesture
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

      const maxScrollLeft = slider.scrollWidth - slider.clientWidth;
      if (maxScrollLeft <= 0) return;

      const canScrollLeft = slider.scrollLeft > 0;
      const canScrollRight = slider.scrollLeft < maxScrollLeft;

      // Only hijack when horizontal scroll is possible (so parent keeps priority)
      if (!((e.deltaY < 0 && canScrollLeft) || (e.deltaY > 0 && canScrollRight))) return;

      e.preventDefault();

      // Temporarily disable snap while wheeling (fixes “sometimes works”)
      if (!isSnappingDisabled) {
        slider.style.scrollSnapType = 'none';
        isSnappingDisabled = true;
      }

      // Normalize delta (deltaMode can be lines/pages on some devices)
      const delta =
        e.deltaMode === 1 ? e.deltaY * 16 :
        e.deltaMode === 2 ? e.deltaY * slider.clientWidth :
        e.deltaY;

      slider.scrollLeft += delta;

      // Restore snap shortly after scrolling ends
      clearTimeout(snapRestoreTimer);
      snapRestoreTimer = setTimeout(() => {
        slider.style.scrollSnapType = '';
        isSnappingDisabled = false;
      }, 140);
    };

    slider.addEventListener('wheel', onWheel, { passive: false });
    cleanup.push(() => slider.removeEventListener('wheel', onWheel));
  });

  return () => cleanup.forEach((fn) => fn());
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    const urlParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())
    if(urlParams.cart === "open"){
      this.open()
    }
    // Init horizontal scroll AFTER element is connected
    this.#initHorizontalScroll();
  }
  #cleanupWheelScroll = null;
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#cleanupWheelScroll?.();
    this.#cleanupWheelScroll = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

    requestAnimationFrame(() => {
      this.#cleanupWheelScroll?.();
      this.#cleanupWheelScroll = enableWheelHorizontalScroll(this);
    });
  }

  close() {
    this.closeDialog();
  }

  /* ------------------------------
     Horizontal Scroll Handling
  ------------------------------ */
  #wheelHandlers = new Map();

  #initHorizontalScroll() {
    try {
      const sliders = this.querySelectorAll(
        '.cart__upsell_collections, .css-slider-feel'
      );

      if (!sliders.length) return;

      sliders.forEach((slider) => {
        const handler = (e) => {
          // Ignore horizontal trackpad scroll
          if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;

          const canScrollLeft = slider.scrollLeft > 0;
          const canScrollRight =
            slider.scrollLeft <
            slider.scrollWidth - slider.clientWidth;

          if (
            (e.deltaY < 0 && canScrollLeft) ||
            (e.deltaY > 0 && canScrollRight)
          ) {
            e.preventDefault();
            slider.scrollLeft += e.deltaY;
          }
        };

        slider.addEventListener('wheel', handler, { passive: false });
        this.#wheelHandlers.set(slider, handler);
      });
    } catch (err) {
      console.warn('[CartDrawer] Horizontal scroll init failed', err);
    }
  }

  #destroyHorizontalScroll() {
    try {
      this.#wheelHandlers.forEach((handler, slider) => {
        slider.removeEventListener('wheel', handler);
      });
      this.#wheelHandlers.clear();
    } catch (err) {
      console.warn('[CartDrawer] Horizontal scroll cleanup failed', err);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
